import React, { useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { Controller, useForm, useWatch } from "react-hook-form";
import { Button, Grid, TextField, Typography } from "@mui/material";

import AppForm from "components/Shared/AppForm";
import AppSwitch from "components/Shared/AppSwitch";
import BaseAutocomplete from "components/Shared/Base/BaseAutocomplete";

import { useAuth } from "hooks/useAuth";
import { useGetDDLAccountListQuery } from "store/services/accountApi";
import { useLazyGetDDLEntityListQuery } from "store/services/entityApi";
import { useLazyGetCountryDDLQuery } from "store/services/countryApi";

const INITIAL_STATE = {
  account: null,
  entity: null,
  name: "",
  url: "",
  country: null,
  isActive: true,
  isDefault: false,
  relayConfig: {
    enabled: false,
    baseUrl: "",
    authHeaderName: "",
    authHeaderValue: "",
    targetBaseUrlHeader: "X-Forward-To-Base-Url",
  },
};

const EBillProviderForm = ({
  isBusy = false,
  eBillProviderDetail = null,
  onSubmit = () => { },
}) => {
  const navigate = useNavigate();
  const { viewEntity, isAccountAdmin, isSystemAdmin } = useAuth();

  // RTK Queries
  const { data: accounts, isLoading: loadingAccounts } =
    useGetDDLAccountListQuery();
  const [fetchEntities, { data: entities, isLoading: loadingEntities }] =
    useLazyGetDDLEntityListQuery();
  const [fetchCountries, { data: countries, isLoading: loadingCountries }] =
    useLazyGetCountryDDLQuery();

  const {
    control,
    handleSubmit,
    setValue,
    reset,
    formState: { errors },
  } = useForm({
    defaultValues: {
      ...INITIAL_STATE,
      entity: viewEntity?.entity || null,
      account: viewEntity?.entity?.account || null,
    },
    mode: "onChange",
  });

  const _watchAccount = useWatch({ control, name: "account" });
  const relayEnabled = useWatch({ control, name: "relayConfig.enabled" });

  const handleOnSubmit = (data) => {
    const formattedData = {
      ...data,
      account: data?.account?._id,
      entity: data?.entity?._id,
      country: data?.country?._id,
      relayConfig: {
        enabled: Boolean(data?.relayConfig?.enabled),
        baseUrl: data?.relayConfig?.baseUrl?.trim() || "",
        authHeaderName: data?.relayConfig?.authHeaderName?.trim() || "",
        authHeaderValue: data?.relayConfig?.authHeaderValue?.trim() || "",
        targetBaseUrlHeader:
          data?.relayConfig?.targetBaseUrlHeader?.trim() ||
          "X-Forward-To-Base-Url",
      },
    };
    onSubmit(formattedData);
  };

  const isMounted = useRef(true);

  useEffect(() => {
    if (!isMounted.current) return;

    if (!eBillProviderDetail) {
      reset({
        ...INITIAL_STATE,
        entity: viewEntity?.entity || null,
        account: viewEntity?.entity?.account || null,
      });
      return;
    }

    reset({
      ...INITIAL_STATE,
      ...eBillProviderDetail,
      account: eBillProviderDetail.account || null,
      entity: eBillProviderDetail.entity || null,
      country: eBillProviderDetail.country || null,
      relayConfig: {
        ...INITIAL_STATE.relayConfig,
        ...(eBillProviderDetail?.relayConfig || {}),
      },
    });

    return () => {
      isMounted.current = false;
    };
  }, [eBillProviderDetail, reset, viewEntity]);

  // Fetch entities when account changes
  useEffect(() => {
    if (
      (isAccountAdmin || isSystemAdmin) &&
      _watchAccount?._id &&
      isMounted.current
    ) {
      fetchEntities({ account: _watchAccount._id });
    }
  }, [_watchAccount?._id, fetchEntities, isAccountAdmin, isSystemAdmin]);

  // Fetch countries on mount
  useEffect(() => {
    fetchCountries();
  }, [fetchCountries]);

  return (
    <AppForm loading={isBusy}>
      <AppForm.Content>
        <Grid container spacing={4}>
          {/* Account */}
          {(isAccountAdmin || isSystemAdmin) && (
            <Grid item md={6}>
              <Controller
                name="account"
                control={control}
                rules={{ required: "Please select an account." }}
                render={({ field }) => (
                  <BaseAutocomplete
                    {...field}
                    fullWidth
                    label="Account"
                    required
                    value={field.value ?? null}
                    onChange={(_, data) => {
                      field.onChange(data);
                      setValue("entity", null);
                    }}
                    getOptionLabel={(option) => option?.name ?? ""}
                    isOptionEqualToValue={(option, value) =>
                      option?._id === value?._id
                    }
                    options={accounts || []}
                    loading={loadingAccounts}
                    loadingText="Loading Accounts..."
                    disabled={isAccountAdmin}
                    helperText={errors?.account?.message}
                    error={!!errors?.account}
                  />
                )}
              />
            </Grid>
          )}

          {/* Entity */}
          {(isAccountAdmin || isSystemAdmin) && (
            <Grid item md={6}>
              <Controller
                name="entity"
                control={control}
                rules={{ required: "Please select an entity." }}
                render={({ field: { value, onChange } }) => (
                  <BaseAutocomplete
                    fullWidth
                    required
                    label="Entity"
                    value={value ?? null}
                    onChange={(_, data) => onChange(data)}
                    getOptionLabel={(option) => option?.name ?? ""}
                    isOptionEqualToValue={(option, value) =>
                      option?._id === value?._id
                    }
                    options={entities || []}
                    loading={loadingEntities}
                    loadingText="Loading Entities..."
                    helperText={errors?.entity?.message}
                    error={!!errors?.entity}
                  />
                )}
              />
            </Grid>
          )}

          {/* Provider Name */}
          <Grid item xs={12} sm={6}>
            <Controller
              name="name"
              control={control}
              rules={{ required: "Provider name is required." }}
              render={({ field }) => (
                <TextField
                  {...field}
                  required
                  fullWidth
                  label="Provider Name"
                  size="small"
                  error={!!errors.name}
                  helperText={errors.name?.message}
                />
              )}
            />
          </Grid>

          {/* Base URL */}
          <Grid item xs={12} sm={6}>
            <Controller
              name="url"
              control={control}
              rules={{
                required: "Base URL is required.",
                pattern: {
                  value: /^https?:\/\/.+/,
                  message: "Enter a valid URL (http:// or https://)",
                },
              }}
              render={({ field }) => (
                <TextField
                  {...field}
                  required
                  fullWidth
                  label="Base URL"
                  size="small"
                  placeholder="https://api.provider.com"
                  error={!!errors.url}
                  helperText={errors.url?.message}
                />
              )}
            />
          </Grid>

          {/* Country */}
          <Grid item xs={12} sm={6}>
            <Controller
              name="country"
              control={control}
              render={({ field: { value, onChange } }) => (
                <BaseAutocomplete
                  fullWidth
                  label="Country"
                  value={value ?? null}
                  onChange={(_, data) => onChange(data)}
                  getOptionLabel={(option) => option?.name ?? ""}
                  isOptionEqualToValue={(option, value) =>
                    option?._id === value?._id
                  }
                  options={countries || []}
                  loading={loadingCountries}
                  loadingText="Loading Countries..."
                  helperText={errors?.country?.message}
                  error={!!errors?.country}
                />
              )}
            />
          </Grid>

          {/* isDefault & isActive - only on edit */}
          {eBillProviderDetail && (
            <>
              <Grid item xs={6} sm={3}>
                <Controller
                  name="isDefault"
                  control={control}
                  render={({ field }) => (
                    <AppSwitch {...field} label="Is Default?" />
                  )}
                />
              </Grid>
              <Grid item xs={6} sm={3}>
                <Controller
                  name="isActive"
                  control={control}
                  render={({ field }) => (
                    <AppSwitch {...field} label="Is Active?" />
                  )}
                />
              </Grid>
            </>
          )}

          {/* VPC Relay */}
          <Grid item xs={12}>
            <Typography variant="h6">VPC Relay</Typography>
          </Grid>

          <Grid item xs={12} sm={6}>
            <Controller
              name="relayConfig.enabled"
              control={control}
              render={({ field }) => (
                <AppSwitch
                  {...field}
                  checked={!!field.value}
                  label="Use VPC relay?"
                />
              )}
            />
          </Grid>

          <Grid item xs={12} sm={6}>
            <Controller
              name="relayConfig.targetBaseUrlHeader"
              control={control}
              render={({ field, fieldState }) => (
                <TextField
                  {...field}
                  fullWidth
                  size="small"
                  label="Target Base URL Header"
                  placeholder="X-Forward-To-Base-Url"
                  error={!!fieldState.error}
                  helperText={
                    fieldState.error?.message ||
                    "Header used by the relay to read the real provider base URL"
                  }
                />
              )}
            />
          </Grid>

          {relayEnabled && (
            <>
              <Grid item xs={12} sm={6}>
                <Controller
                  name="relayConfig.baseUrl"
                  control={control}
                  rules={{
                    validate: (value) =>
                      !relayEnabled || value?.trim()
                        ? true
                        : "Relay base URL is required when relay is enabled.",
                  }}
                  render={({ field, fieldState }) => (
                    <TextField
                      {...field}
                      fullWidth
                      size="small"
                      required={relayEnabled}
                      label="Relay Base URL"
                      placeholder="https://vpc-relay.example.com"
                      error={!!fieldState.error}
                      helperText={fieldState.error?.message}
                    />
                  )}
                />
              </Grid>

              <Grid item xs={12} sm={6}>
                <Controller
                  name="relayConfig.authHeaderName"
                  control={control}
                  render={({ field, fieldState }) => (
                    <TextField
                      {...field}
                      fullWidth
                      size="small"
                      label="Relay Auth Header Name"
                      placeholder="X-Internal-Relay-Token"
                      error={!!fieldState.error}
                      helperText={
                        fieldState.error?.message ||
                        "Optional internal header used to authenticate this app to the relay"
                      }
                    />
                  )}
                />
              </Grid>

              <Grid item xs={12} sm={6}>
                <Controller
                  name="relayConfig.authHeaderValue"
                  control={control}
                  render={({ field, fieldState }) => (
                    <TextField
                      {...field}
                      fullWidth
                      size="small"
                      label="Relay Auth Header Value"
                      placeholder="Shared secret for the VPC relay"
                      error={!!fieldState.error}
                      helperText={fieldState.error?.message}
                    />
                  )}
                />
              </Grid>
            </>
          )}
        </Grid>
      </AppForm.Content>

      <AppForm.Actions>
        <Button onClick={() => navigate(-1)}>Cancel</Button>
        <Button
          variant="contained"
          onClick={handleSubmit(handleOnSubmit)}
          disabled={isBusy}
        >
          {eBillProviderDetail ? "Update Provider" : "Add Provider"}
        </Button>
      </AppForm.Actions>
    </AppForm>
  );
};

export default EBillProviderForm;